import QRCode from "qrcode";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { useModalLayer } from "../../components/ModalLayer";
import { CustomerQrActions, CustomerQrState } from "./customer-qr-controller";

/** Big enough to scan off a screen or a printed card at arm's length. */
const QR_PIXELS = 320;

/**
 * The shop's customer entry point as a QR code and a link, as iOS's
 * `AdminShopQrView`: customers scan it on the counter and land in the shop. The
 * same URL is offered as text for a message, an email or a printed sign.
 */
export function ShopQr({ shopName }: { shopName: string | null }) {
  const state = useSubtree(CustomerQrState);
  const actions = useSubtree(CustomerQrActions);
  const observer = useObserver();
  const url = observer.watch(state.url);
  const status = observer.watch(state.status);
  const shop = shopName?.trim() || "your shop";

  const [built, setBuilt] = useState<{ url: string; image: string | null } | null>(null);
  const [copied, setCopied] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    // Rendered in the browser: the URL is not a secret, and this avoids a round trip.
    QRCode.toDataURL(url, { width: QR_PIXELS, margin: 2, errorCorrectionLevel: "M" })
      .then((image) => {
        if (!cancelled) setBuilt({ url, image });
      })
      .catch(() => {
        if (!cancelled) setBuilt({ url, image: null });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // The OS share sheet, where there is one. Absent on most desktop browsers, so Copy is always offered.
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const share = async () => {
    if (!url) return;
    try {
      await navigator.share({ title: "Scan cards in our shop", url });
    } catch {
      // A dismissed share sheet rejects; nothing to report.
    }
  };

  const qr = built && built.url === url ? built : null;
  return (
    <div className="flex flex-col items-center gap-4 text-center">
      <p className="text-sm text-muted">{`Point a phone camera at this to open ${shop}.`}</p>
      {/* Stays white while the rest of the app is near-black: a scanner needs that contrast. */}
      <div
        ref={box}
        tabIndex={-1}
        className="flex aspect-square w-full max-w-[260px] items-center justify-center rounded-row bg-white p-3 outline-none"
      >
        {status.kind === "error" ? (
          <span role="alert" className="px-6 text-sm text-danger">
            Couldn't load your shop link.
          </span>
        ) : !qr ? (
          <span className="text-sm text-black/50">Fetching your link…</span>
        ) : qr.image ? (
          <img src={qr.image} alt={`QR code that opens ${shop}`} className="h-full w-full" />
        ) : (
          <span className="px-6 text-sm text-danger">Couldn't build a QR code for that link.</span>
        )}
      </div>
      {/* The button goes while the link is read, so the focus waits on the code's box. */}
      {status.kind === "error" && (
        <button
          type="button"
          onClick={() => {
            box.current?.focus();
            actions.retry();
          }}
          className="btn-secondary btn-sm"
        >
          Try again
        </button>
      )}
      {url && (
        <p className="w-full select-all break-all rounded-btn bg-surface px-3 py-2 font-mono text-xs text-muted">
          {url}
        </p>
      )}
      <div className="flex w-full gap-2">
        <button
          type="button"
          onClick={copy}
          disabled={!url}
          className={`${canShare ? "btn-secondary" : "btn-primary"} btn-sm flex-1`}
        >
          {copied ? "Link copied" : "Copy link"}
        </button>
        {canShare && (
          <button type="button" onClick={share} disabled={!url} className="btn-primary btn-sm flex-1">
            Share
          </button>
        )}
      </div>
      {qr?.image && (
        <a href={qr.image} download="shop-qr-code.png" className="text-xs text-muted underline hover:text-white">
          Download the QR code
        </a>
      )}
    </div>
  );
}

/** Settings' "Show QR code": the same QR in a dialog, as iOS's "Your shop QR" sheet. Renders nothing while closed. */
export function CustomerQrModal({ shopName = null }: { shopName?: string | null }) {
  const state = useSubtree(CustomerQrState);
  const observer = useObserver();
  const storeId = observer.watch(state.storeId);
  if (!storeId) return null;
  return <Dialog shopName={shopName} />;
}

function Dialog({ shopName }: { shopName: string | null }) {
  const actions = useSubtree(CustomerQrActions);
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // The dialog itself takes the focus, so it opens at its top on a short screen, where it scrolls.
  useModalLayer(panel, actions.close);

  return createPortal(
    <div className="scrim items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={actions.close}
        className="absolute inset-0 cursor-default"
      />
      <div
        ref={panel}
        tabIndex={-1}
        className="modal relative max-h-[calc(100dvh-2rem)] w-full max-w-sm overflow-y-auto p-6 outline-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="mb-4 text-base font-semibold text-white">
          Your shop QR
        </h2>
        <ShopQr shopName={shopName} />
        <button type="button" onClick={actions.close} className="btn-ghost mt-3 w-full justify-center">
          Close
        </button>
      </div>
    </div>,
    document.body,
  );
}
